import { useCallback, useMemo } from 'react';

import { Native } from '@givebacks/api';
import { useListUserCauseSelections } from '@givebacks/api/core';
import { usePlatformService } from 'core/Hooks';
import { useNavigate } from 'react-router-dom';
import { Button, LoadingOverlay, Modal, ModalProps, ScrollArea, Stack, Text } from 'ui';

import { useUserIntents } from '../../../../../hooks/onboarding/useUserIntents';
import {
  ConnectedCauseCard,
  ConnectedCausesCombinedCard,
} from '../../../../molecules/cards/ConnectedCauseCard/ConnectedCauseCard';

interface YourPersonalizedStartModalProps extends ModalProps {}

const YourPersonalizedStartContent = () => {
  const { data: userCauseSelections } = useListUserCauseSelections();
  const { allCauseIntents, isLoading } = useUserIntents();
  const navigate = useNavigate();
  const Platform = usePlatformService();
  /**
   * Combines the causes logo into one array
   * if the cause has a logo, it will be added to the array
   * if the cause does not have a logo, a default logo will be added to the end of the array
   */
  const combinedCauseLogos = useMemo(() => {
    const logos: string[] = [];
    const causesWithLogos = userCauseSelections
      ?.filter(
        (causeSelection) =>
          causeSelection.cause.logo?.url && causeSelection.cause.logo.default != true
      )
      .map((cause) => cause.cause.logo?.url)
      .filter((logo) => logo !== undefined);

    if (causesWithLogos) {
      logos.push(...causesWithLogos);
    }
    if (userCauseSelections?.length) {
      const defaultLogos = Array(userCauseSelections?.length - (causesWithLogos?.length || 0)).fill(
        'https://s3.amazonaws.com/com.givebacks.public/images/causes/defaultLogo.png'
      ) as string[];

      logos.push(...defaultLogos);
    }

    return logos;
  }, [userCauseSelections]);

  const cards = useMemo(() => {
    return userCauseSelections?.map((causeSelection) => (
      <ConnectedCauseCard cause={causeSelection.cause} key={causeSelection.cause.uuid} />
    ));
  }, [userCauseSelections]);

  const defaultActions = useMemo(() => {
    return Platform.select({
      default: () => {
        navigate('/onboarding/intent');
      },
      native: () => {
        Native.postAction('navigate', { url: '/native-onboarding/intent' });
      },
    });
  }, [Platform, navigate]);

  const handleExploreMoreActions = useCallback(() => {
    defaultActions();
  }, [defaultActions]);

  if (isLoading) {
    return <LoadingOverlay visible={true} />;
  }

  return (
    <Stack gap="lg" align="center">
      {cards}
      {allCauseIntents && <ConnectedCausesCombinedCard logos={combinedCauseLogos} />}
      <Button size="md" onClick={handleExploreMoreActions} variant="gray">
        Explore More Actions
      </Button>
    </Stack>
  );
};

const YourPersonalizedStartModal = ({ ...props }: YourPersonalizedStartModalProps) => {
  return (
    <Modal title="Your Personalized Start" {...props} scrollAreaComponent={ScrollArea.Autosize}>
      <Stack gap="lg">
        <Text fz="16" fw={500} c="gray.6">
          Start with a few simple steps picked just for you.
        </Text>
        <YourPersonalizedStartContent />
      </Stack>
    </Modal>
  );
};

export { YourPersonalizedStartContent, YourPersonalizedStartModal };
